import {
  colors,
  FormHelperText,
  InputAdornment as MuiInputAdornment,
  InputBaseComponentProps as MuiInputBaseComponentProps,
  LinearProgress as MuiLinearProgress,
  Theme,
} from "@mui/material";
import { createStyles, WithStyles, withStyles } from "@mui/styles";
import {
  action,
  autorun,
  IReactionDisposer,
  makeObservable,
  observable,
} from "mobx";
import { observer } from "mobx-react";
import * as React from "react";
// @ts-ignore
import { IMaskInput } from "react-imask";
import { DialogChildProps } from "../config";
import AppServer from "../core/AppServer";
import Localization from "../core/Localization";
import Sys from "../core/Sys";
import Button from "../coreui/Button";
import getFieldHelperText from "../coreui/FieldHelperText";
import FocusRipple from "../coreui/FocusRipple";
import Icon from "../coreui/Icon";
import IconWithLink from "../coreui/IconWithLink";
import ImageWithLink from "../coreui/ImageWithLink";
import TextField from "../coreui/TextField";
import Typography from "../coreui/Typography";
import PaneRow, { RuntimeWidgetT } from "../models/PaneRow";
import DocumentEditService, {
  CompleteDocumentUploadResponse,
} from "../services/DocumentEditService";
import DocumentService, {
  DocumentUploadResponse,
} from "../services/DocumentService";
import ErrorsStore from "../stores/ErrorsStore";
import PaneDataStore from "../stores/PaneDataStore";
import PendingDocumentStore from "../stores/PendingDocumentStore";
import RequestsStore from "../stores/RequestsStore";
import { AccessLevel } from "./AccessLevel";

interface ConfigProperties {
  dataId: string;
  disabledHelpText: string;
  helperText: string;
  label: string;
  maxFileBytes: number | null;
  maxFileBytesError: string;
  name: string;
  nameRequiredError: string;
  propagated: DialogChildProps | null;
  validExtensions: string[];
  validExtensionsError: string;
}

interface State {
  isDocumentUploadFinished?: boolean;
  isDocumentUploading?: boolean;
  isDragging?: boolean;
  isFocused?: boolean;
  isUploadButtonFocused: boolean;
  localErrors?: string[];
  newDocumentExtension?: string;
  newDocumentFileName?: string;
  uploadPercentComplete?: number;
}

interface RuntimeProperties {
  accessLevel: AccessLevel;
  businessErrors: string[];
  canDelete: boolean;
  documentExtension: string | null;
  documentHandle: string | null;
  downloadToken: string;
  extensionIconName: string | null;
  pendingDocumentId: number | null;
  pendingThumbnailId: number | null;
  showAsMandatory: boolean;
  showDisabledHelp: boolean;
  thumbnailType: "Medium" | "ExtensionIcon" | null;
}

const styles = (theme: Theme) =>
  createStyles({
    cellLeft: {
      width: 64,
    },
    cellRight: {
      flex: 1,
      minWidth: 0,
    },
    dropArea: {
      alignItems: "center",
    },
    editableRoot: {
      outline: "none",
      position: "relative",
      width: "100%",
    },
    fileInput: {
      cursor: "pointer",
      height: "100%",
      left: 0,
      opacity: 0,
      position: "absolute",
      top: 0,
      width: "100%",
    },
    input: {
      backgroundColor: `${theme.palette.grey[200]} !important`,
    },
    inputLabelRoot: {
      width: "calc(100% - 72px)",
    },
    inputLabelShrink: {
      width: "calc((100% - 72px) * 1.333)",
    },
    label: {
      width: "calc(100% - 72px)",
    },
    previewButton: {
      marginRight: 16,
    },
    progressBar: {
      backgroundColor: colors.lightGreen[100],
      bottom: 0,
      height: 4,
      position: "absolute",
      width: "100%",
    },
    progressBarFilled: {
      backgroundColor: colors.lightGreen[500],
      transitionDuration: "0.1s",
    },
    progressBarFilledSlow: {
      backgroundColor: colors.lightGreen[500],
      transitionDuration: "5s",
    },
    readOnlyRoot: {
      display: "flex",
    },
    row: {
      alignItems: "center",
      display: "flex",
      flexDirection: "row",
    },
    uploadCompleteIcon: {
      bottom: -24,
      fontSize: 16,
      right: 0,
    },
  });

export class DocumentEdit extends React.Component<
  ConfigProperties & WithStyles<typeof styles>,
  State
> {
  private readonly componentId: string;
  private customInputRef: { maskValue: string };
  private dataId: string = "";
  private dialogRowKey?: string;
  private dragEnterStack = 0;
  private readonly helperTextId: string;
  private lastDocumentHandle: string | null;
  private name: string = "";
  private rootRef = React.createRef<HTMLDivElement>();
  private saveMonitorDisposer: IReactionDisposer;

  private createCustomInput = React.forwardRef(
    (
      props: MuiInputBaseComponentProps,
      inputRef: React.ForwardedRef<HTMLInputElement>
    ): JSX.Element => {
      const { extension, onChange, ...other } = props;

      const showExtension: boolean =
        !!extension && (!!props.value || this.state.isFocused!);

      return (
        <IMaskInput
          {...other}
          blocks={{ "|": { mask: /.*/ } }}
          inputRef={inputRef}
          lazy={false}
          mask={showExtension ? `|.${extension}` : "|"}
          // The onChange event doesn't fire when lazy == true and the
          // onAccept event fires when the value is programatically
          // changed, so we use onInput
          onInput={onChange}
          placeholderChar=" "
          ref={(ref: { maskValue: string }) => {
            this.customInputRef = ref;
          }}
          unmask={true}
        />
      );
    }
  );

  public constructor(props: ConfigProperties & WithStyles<typeof styles>) {
    super(props);

    makeObservable<DocumentEdit, "dataId" | "name" | "syncDerivedWithProps">(
      this,
      {
        dataId: observable,
        name: observable,
        syncDerivedWithProps: action,
      }
    );

    this.componentId = `document-edit-${Sys.nextId}`;
    this.helperTextId = `${this.componentId}-helper-text`;

    this.state = {
      isDocumentUploadFinished: false,
      isDocumentUploading: false,
      isFocused: false,
      isUploadButtonFocused: false,
      localErrors: [],
      uploadPercentComplete: 0,
    };

    this.syncDerivedWithProps();
  }

  private announceErrors(errors: string[]): void {
    if (errors.length > 0) {
      Sys.announce(errors.join("; "));
    }
  }

  private completeDocumentUpload(
    uploadResponse: DocumentUploadResponse,
    file: File
  ): Promise<void> {
    RequestsStore.instance.processingStarted(
      Localization.getBuiltInMessage("uploadDone")
    );

    this.setState({ uploadPercentComplete: 100 });

    const row: PaneRow = PaneRow.get(this.dataId)!;
    return DocumentEditService.completeDocumentUpload(
      row.rowKey,
      this.dataId,
      this.name,
      this.dialogRowKey,
      uploadResponse.pendingDocumentId,
      uploadResponse.pendingThumbnailId
    )
      .then((response: CompleteDocumentUploadResponse) => {
        AppServer.setState(response.appServerState);
        PaneDataStore.loadResponse(response.paneDataByDataId);

        if (response.widgetData) {
          PaneDataStore.loadWidgetData(this.dataId, response.widgetData);
        }

        if (response.uploadErrors.length > 0) {
          this.uploadError(file, response.uploadErrors);
          return;
        }

        const updatedRow = PaneRow.get(this.dataId)!;
        const widget = updatedRow.getWidgetT<string | null, RuntimeProperties>(
          this.name
        );

        widget.setValue(this.state.newDocumentFileName || null);
        this.setState({
          isDocumentUploadFinished: true,
          isDocumentUploading: false,
          uploadPercentComplete: 100,
        });

        Sys.announce(
          Localization.getBuiltInMessage("DocumentUpload.UploadComplete")
        );
      })
      .finally(() => RequestsStore.instance.processingStopped());
  }

  private deleteDocument = () => {
    this.setState({ isDocumentUploadFinished: false });

    const row: PaneRow = PaneRow.get(this.dataId)!;
    row.revertWidgetValue(this.name); // Reset any changes to the file name

    DocumentEditService.deleteDocument(
      row.rowKey,
      this.dataId,
      this.name,
      this.dialogRowKey
    )
      .then(() => {
        this.setLocalErrors();
      })
      .catch((reason) => {
        if (reason) {
          throw reason;
        }
      });
  };

  private getBaseName(
    widget: RuntimeWidgetT<string | null, RuntimeProperties>
  ): string | null {
    return this.state.isDocumentUploading
      ? this.state.newDocumentFileName || null
      : widget.value;
  }

  private getErrors(businessErrors: string[]): string[] {
    let result: string[] = [...businessErrors];

    result = result.concat(this.state.localErrors!);

    return result;
  }

  private onChange = () => {
    const value: string = this.customInputRef.maskValue;

    ErrorsStore.clearBusinessErrorsForWidget(this.dataId, this.name);
    this.setState({ localErrors: [] });

    if (this.state.isDocumentUploading) {
      this.setState({ newDocumentFileName: value });
    } else {
      const row = PaneRow.get(this.dataId)!;
      const widget = row.getWidgetT<string | null, RuntimeProperties>(
        this.name
      );
      widget.setValue(value === "" ? null : value);
    }
  };

  private onClickFileUpload = () => {
    this.setState({
      isDocumentUploadFinished: false,
      uploadPercentComplete: 0,
    });
  };

  private onDragEnterIntoDocument = () => {
    this.dragEnterStack++;

    const row = PaneRow.get(this.dataId);
    if (!row) {
      return;
    }

    const widget = row.getWidgetT<string | null, RuntimeProperties>(this.name);

    if (
      widget.properties.accessLevel >= AccessLevel.enterable &&
      !widget.properties.canDelete &&
      !this.state.isDocumentUploading
    ) {
      this.setState({ isDragging: true });
    }
  };

  private onDragLeaveOutOfDocument = () => {
    this.dragEnterStack--;

    if (this.dragEnterStack < 1) {
      this.setState({ isDragging: false });
    }
  };

  private onDragOverIntoWidget = (event: React.DragEvent<HTMLDivElement>) => {
    event.dataTransfer.dropEffect = "copy";
    event.preventDefault();
    event.stopPropagation();

    return false;
  };

  private onDropIntoDocument = (event: DragEvent) => {
    // Delay so the widget drop event fires (delay the re-render)
    setTimeout(() => {
      this.dragEnterStack = 0;
      this.setState({
        isDocumentUploadFinished: false,
        isDragging: false,
        uploadPercentComplete: 0,
      });
    });

    event.preventDefault();

    return false;
  };

  private onDropIntoWidget = (event: React.DragEvent<HTMLDivElement>) => {
    this.uploadFile(event.dataTransfer.files);
  };

  private onFileSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target;

    if (!input.files || input.files.length <= 0) {
      return;
    }

    ErrorsStore.clearBusinessErrorsForWidget(this.dataId, this.name);
    this.uploadFile(input.files);
  };

  private setLocalErrors() {
    const localErrors: string[] = [];
    const row = PaneRow.get(this.dataId)!;
    const widget = row.getWidgetT<string | null, RuntimeProperties>(this.name);

    if (
      !this.getBaseName(widget) &&
      (widget.properties.documentHandle !== null ||
        this.state.isDocumentUploading)
    ) {
      localErrors.push(this.props.nameRequiredError);
    }

    this.setState({ localErrors });
  }

  private syncDerivedWithProps() {
    this.dataId = this.props.dataId;
    this.name = this.props.name;
    this.dialogRowKey =
      this.props.propagated?.parentDialog?.rowKey || undefined;
  }

  private updateProgress = (file: File, event: ProgressEvent) => {
    // Only show 90% complete until the upload has finished
    const percent = Math.round((event.loaded / event.total) * 90);
    this.setState((prevState) => {
      if (event.loaded / event.total >= 1) {
        return { uploadPercentComplete: 98 };
      }

      if (percent > prevState.uploadPercentComplete! && percent <= 90) {
        return { uploadPercentComplete: percent };
      }

      return {};
    });
  };

  private uploadError = (file: File | null, errors: string[]) => {
    if (file) {
      ErrorsStore.showErrors(errors.map((error) => `${file.name} - ${error}`));
    } else {
      ErrorsStore.showErrors(errors);
    }
    this.setState({ isDocumentUploading: false });
  };

  private uploadFile(files: FileList) {
    if (files.length !== 1) {
      this.uploadError(null, [Localization.getBuiltInMessage("singleUpload")]);

      return;
    }

    const file = files[0];
    const fullFileName = file.name;
    const index = fullFileName.lastIndexOf(".");
    const extension = index > 0 ? fullFileName.substring(index + 1) : "";

    if (
      this.props.validExtensions.length > 0 &&
      this.props.validExtensions.indexOf(extension.toLowerCase()) < 0
    ) {
      const error = this.props.validExtensionsError.replace(
        "{extension}",
        extension
      );
      this.uploadError(file, [error]);

      return;
    }

    if (
      this.props.maxFileBytes !== null &&
      file.size > this.props.maxFileBytes
    ) {
      this.uploadError(file, [this.props.maxFileBytesError]);

      return;
    }

    this.setState(
      {
        isDocumentUploading: true,
        newDocumentExtension: extension,
        newDocumentFileName:
          index > 0 ? fullFileName.substring(0, index) : fullFileName,
      },
      () => {
        this.setLocalErrors();
        this.rootRef.current!.focus();
      }
    );

    Sys.announce(
      Localization.getBuiltInMessage("DocumentUpload.UploadStarted")
    );
    const batch = DocumentService.uploadFiles(
      this.props.dataId,
      this.props.name,
      [file],
      this.updateProgress,
      this.uploadError
    );

    Promise.all(batch).then((responses: DocumentUploadResponse[]) => {
      const response = responses[0];
      if (
        !PendingDocumentStore.isRequestAborted(file["rowKey"]) &&
        response.uploadErrors.length <= 0
      ) {
        this.completeDocumentUpload(response, file);
        this.setState({ newDocumentExtension: response.extension });
        PendingDocumentStore.removePendingDocument(file["rowKey"]);
      } else {
        // Handle aborted request
        PendingDocumentStore.deleteAbortedRequest(file["rowKey"]);
      }
    });
  }

  public componentDidMount(): void {
    document.body.addEventListener("dragenter", this.onDragEnterIntoDocument);
    document.body.addEventListener("dragleave", this.onDragLeaveOutOfDocument);
    document.body.addEventListener("drop", this.onDropIntoDocument);

    // Monitor for when the presentation is saved after a document upload
    this.saveMonitorDisposer = autorun(() => {
      const row = PaneRow.get(this.dataId);
      if (!row) {
        return;
      }

      const widget = row.getWidgetT<string | null, RuntimeProperties>(
        this.name
      );

      if (this.lastDocumentHandle !== widget.properties.documentHandle) {
        this.lastDocumentHandle = widget.properties.documentHandle;
        this.setState({ isDocumentUploadFinished: false });
      }
    });
  }

  public componentDidUpdate(): void {
    this.syncDerivedWithProps();
  }

  public componentWillUnmount(): void {
    document.body.removeEventListener(
      "dragenter",
      this.onDragEnterIntoDocument
    );
    document.body.removeEventListener(
      "dragleave",
      this.onDragLeaveOutOfDocument
    );
    document.body.removeEventListener("drop", this.onDropIntoDocument);

    this.saveMonitorDisposer();

    PendingDocumentStore.clearAllPendingDocuments();
  }

  public render(): React.ReactNode {
    const row = PaneRow.get(this.props.dataId);
    if (!row) {
      return null;
    }

    const widget = row.getWidgetT<string | null, RuntimeProperties>(
      this.props.name
    );

    if (widget.properties.accessLevel === AccessLevel.hidden) {
      return null;
    }

    if (widget.properties.accessLevel === AccessLevel.disabled) {
      return (
        <TextField
          disabled={true}
          disabledHelpText={
            widget.properties.showDisabledHelp
              ? this.props.disabledHelpText
              : undefined
          }
          label={this.props.label}
          name={this.props.name}
          variant="filled"
        />
      );
    }

    let documentExtension = widget.properties.documentExtension;
    if (this.state.newDocumentExtension) {
      documentExtension = this.state.newDocumentExtension;
    }
    const documentBaseName = this.getBaseName(widget);

    const documentUrl = DocumentService.getDocumentUrl(
      widget.properties.documentHandle,
      widget.properties.pendingDocumentId,
      documentBaseName ? `${documentBaseName}.${documentExtension}` : "",
      widget.properties.downloadToken
    );

    let previewButton: React.ReactNode | null = null;

    switch (widget.properties.thumbnailType) {
      case "Medium":
        previewButton = (
          <ImageWithLink
            alternateText={Localization.getBuiltInMessage("viewDocument")}
            disableFocusRipple={true}
            fit="cover"
            height={48}
            href={documentUrl}
            imgSrc={DocumentService.getThumbnailUrl(
              widget.properties.documentHandle,
              widget.properties.pendingDocumentId,
              widget.properties.pendingThumbnailId,
              "Medium Thumbnail",
              widget.properties.downloadToken
            )}
            width={48}
          />
        );
        break;
      case "ExtensionIcon":
        previewButton = (
          <IconWithLink
            alternateText={Localization.getBuiltInMessage("viewDocument")}
            disableFocusRipple={true}
            height={48}
            href={documentUrl}
            icon={widget.properties.extensionIconName!}
            width={48}
          />
        );
        break;
      default:
    }

    const { classes, ..._props } = this.props;

    if (widget.properties.accessLevel === AccessLevel.readOnly) {
      const formattedDocumentName: string | null = documentBaseName
        ? `${documentBaseName}.${documentExtension}`
        : "-";

      return (
        <div className={classes.readOnlyRoot}>
          <div className={classes.row}>
            <div className={classes.cellLeft}>{previewButton}</div>
            <div className={classes.cellRight}>
              <TextField
                label={_props.label}
                name={_props.name}
                readOnly={true}
                value={formattedDocumentName}
                variant="filled"
              />
            </div>
          </div>
        </div>
      );
    }

    const fieldHelperText = getFieldHelperText({
      getErrors: () => this.getErrors(widget.properties.businessErrors),
      helperText: this.props.helperText,
    });

    const helperTextId: string | undefined = fieldHelperText.helperText
      ? this.helperTextId
      : undefined;

    let actionButton: React.ReactNode;
    if (widget.properties.canDelete) {
      actionButton = (
        <Button
          aria-label={Localization.getBuiltInMessage("deleteFile")}
          color="danger"
          disabled={this.state.isDocumentUploading}
          icon="fas fa-trash"
          onClick={this.deleteDocument}
          size="small"
        />
      );
    } else {
      // VERSION_WARNING Material-UI 4.9.14
      // aria-labelledby value is assuming that the id of the label
      // element will continue to following the existing format.
      actionButton = (
        <React.Fragment>
          <Button
            aria-hidden
            component="label"
            disabled={this.state.isDocumentUploading}
            fab
            size="small"
            tabIndex={-1}
            // Address TypeScript error for htmlFor prop, which is not
            // recognized by the button component but is forwarded on to the
            // label element that gets rendered.
            {...({ htmlFor: `${this.componentId}-file-upload` } as any)}
          >
            <FocusRipple visible={this.state.isUploadButtonFocused} />
            <Icon icon="fas fa-upload" />
          </Button>
          <input
            accept={this.props.validExtensions
              .map((extension) => `.${extension}`)
              .join(",")}
            aria-describedby={helperTextId}
            aria-invalid={fieldHelperText.hasErrors}
            aria-labelledby={`${this.componentId}-label`}
            className={classes.fileInput}
            id={`${this.componentId}-file-upload`}
            multiple={false}
            onBlur={() => {
              this.announceErrors(fieldHelperText.errors);
              this.setState({ isUploadButtonFocused: false });
            }}
            onFocus={() => {
              this.setState({ isUploadButtonFocused: true });
            }}
            onChange={this.onFileSelected}
            onMouseDown={this.onClickFileUpload}
            required={widget.properties.showAsMandatory}
            title={Localization.getBuiltInMessage("chooseFile")}
            type="file"
          />
        </React.Fragment>
      );
    }

    const showProgress: boolean =
      this.state.isDocumentUploading || this.state.isDocumentUploadFinished!;
    const uploadPercentComplete = this.state.uploadPercentComplete!;
    const progressBarStyle: string =
      uploadPercentComplete > 90 && uploadPercentComplete < 100
        ? classes.progressBarFilledSlow
        : classes.progressBarFilled;

    const textField = (
      <div style={{ position: "relative" }}>
        <TextField
          disabled={
            (widget.properties.documentHandle === null &&
            !this.state.isDocumentUploading) ||
            !documentBaseName
          }
          error={fieldHelperText.hasErrors}
          id={this.componentId}
          InputLabelProps={{
            classes: {
              root: classes.inputLabelRoot,
              shrink: classes.inputLabelShrink,
            },
            disabled: false,
          }}
          InputProps={{
            "aria-describedby": helperTextId,
            classes: { root: classes.input },
            endAdornment: (
              <MuiInputAdornment position="end" style={{ marginTop: -4 }}>
                {actionButton}
              </MuiInputAdornment>
            ),
            inputComponent: this.createCustomInput,
            inputProps: { extension: documentExtension },
          }}
          label={this.props.label}
          name={this.props.name}
          onBlur={() => {
            this.setLocalErrors();
            this.announceErrors(fieldHelperText.errors);
            this.setState({ isFocused: false });
          }}
          onChange={this.onChange}
          onFocus={() => {
            this.setState({ isFocused: true });
          }}
          required={widget.properties.showAsMandatory}
          value={documentBaseName || ""}
          variant="filled"
        />
        {showProgress ? (
          <React.Fragment>
            <MuiLinearProgress
              aria-hidden={true}
              classes={{
                barColorPrimary: progressBarStyle,
                root: classes.progressBar,
              }}
              variant="determinate"
              value={uploadPercentComplete}
            />
            <Icon
              classes={{ root: classes.uploadCompleteIcon }}
              className="cx-progress-complete"
              icon="far fa-check-circle"
              style={{
                opacity: this.state.isDocumentUploadFinished ? 1 : 0,
              }}
            />
          </React.Fragment>
        ) : null}
        {this.state.isDragging ? (
          <div
            className={`${classes.dropArea} cx-drop-mask`}
            onDragOver={this.onDragOverIntoWidget}
            onDrop={this.onDropIntoWidget}
          >
            <Typography
              style={{
                display: "inline-block",
                fontSize: 20,
                height: "1em",
                width: "1em",
              }}
            >
              <Icon icon="fas fa-cloud-upload" />
            </Typography>
            <Typography
              style={{ fontSize: 12, marginLeft: ".4em" }}
              variant="body2"
            >
              {Localization.getBuiltInMessage("drop")}
            </Typography>
            <Typography style={{ fontSize: 12 }}>
              &nbsp;{Localization.getBuiltInMessage("uploadFile")}
            </Typography>
          </div>
        ) : null}
      </div>
    );

    return (
      <div
        className={classes.editableRoot}
        ref={this.rootRef}
        tabIndex={this.state.isDocumentUploading ? 0 : -1}
      >
        <div className={classes.row}>
          {previewButton && !this.state.isDocumentUploading ? (
            <div className={classes.cellLeft}>{previewButton}</div>
          ) : null}
          <div className={classes.cellRight}>{textField}</div>
        </div>
        {fieldHelperText.helperText ? (
          <div className={classes.row}>
            {previewButton && !this.state.isDocumentUploading ? (
              <div className={classes.cellLeft} />
            ) : null}
            <div className={classes.cellRight}>
              <FormHelperText
                aria-hidden="true"
                children={fieldHelperText.helperText}
                component="div"
                id={helperTextId}
                error={fieldHelperText.hasErrors}
                style={{
                  marginLeft: 16,
                  marginRight: this.state.isDocumentUploadFinished ? 20 : 0,
                }}
                variant="filled"
              />
            </div>
          </div>
        ) : null}
      </div>
    );
  }
}

export default withStyles(styles)(observer(DocumentEdit));
